import { CellRange, DataMap } from '@grapecity/wijmo.grid';
import * as wjGrid from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { FlexGridFilter } from '@grapecity/wijmo.react.grid.filter';
import TextComponent from 'components/Input/TextComponent';
import { useAuth } from 'context/AuthContext';
import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import ScreenTransitionMenu from 'components/Navigation/ScreenTransitionMenu';
import { FlexGridXlsxConverter } from '@grapecity/wijmo.grid.xlsx';
import { getFirstDayOfThisMonth, getLastDayOfNextMonth, getLastDayOfThisMonth } from 'utils/dateUtils';

import InspectionScheduleEditModal from 'views/InspectionScheduleList/InspectionScheduleEditModal';

// 工事
const InspectionScheduleList = (props) => {
  const auth = useAuth();
  const restApi = useRestApi();
  const location = useLocation();
  // 履歴
  const navigate = useNavigate();
  // グリッド
  const flexGrid = useRef();

  // アフター一覧
  const [inspectionRequests, setInspectionRequests] = useState([]);

  // 選択中の工事No
  const [targetKoujiNo, setTargetKoujiNo] = useState();

  // アフター状況モーダルへのパラメタ
  const [inspectionScheduleEditModalParam, setInspectionScheduleEditModalParam] = useState({});

  // 顧客検索条件
  const [searchCondition, setSearchCondition] = useState({
    PLAN_DATE_START: null,
    PLAN_DATE_END: null,
    KEYWORD: '',
  });

  // マスタ情報
  const master = useMaster();
  // 1カ月以内の点検を絞るかチェック
  const [isInspectionWithinOneMonth, setIsInspectionWithinOneMonth] = useState(false);
  // アフター完了分も含めるかチェック
  const [includeCompletedInspections, setIncludeCompletedInspections] = useState(false);

  // 検索処理
  const SearchClients = (condition, targetKoujiNo = null) => {
    const now = new Date();
    const searchCondition = {
      ...condition,
      PLAN_DATE_START: condition.PLAN_DATE_START ?? getDateLimitFromPast(now),
      PLAN_DATE_END: condition.PLAN_DATE_END ? getLastDayOfThisMonth(changeDate(condition.PLAN_DATE_END)) : condition.PLAN_DATE_END,
      INCLUDE_COMPLETED_INSPECTIONS: includeCompletedInspections,
      IS_INSPECTION_WITHIN_ONE_MONTH: isInspectionWithinOneMonth,
      ...(isInspectionWithinOneMonth && {
        ONE_MONTH_START: getFirstDayOfThisMonth(now),
        ONE_MONTH_END: getLastDayOfNextMonth(now),
      }),
    };
    restApi.post('/api/inspectionScheduleList/list', searchCondition, (data) => {
      setInspectionRequests(formatInspectionList(data));
      setTargetKoujiNo(targetKoujiNo);
    });
  };

  //  グリッドの初期化
  function onInitialized(grid) {
    // ヘッダーを2段にする
    var extraRow = new wjGrid.Row();
    extraRow.allowMerging = true;

    var panel = grid.columnHeaders;
    panel.rows.splice(0, 0, extraRow);
    for (let i = 12; i <= 16; i++) {
      panel.setCellData(0, i, `6箇月点検`);
    }
    for (let i = 17; i <= 21; i++) {
      panel.setCellData(0, i, `1年点検`);
    }
    for (let i = 22; i <= 26; i++) {
      panel.setCellData(0, i, `3年点検`);
    }

    // ヘッダーの中央揃え
    for (let i = 0; i < grid.columns.length; i++) {
      grid.columns[i].cssClassAll = 'header-center';
    }
  }
  function formatItem(s, e) {
    const item = e.panel.rows[e.row]?.dataItem;

    if (!item || e.panel.cellType !== wjGrid.CellType.Cell) return;
    const grid = e.panel.grid;
    const col = e.col;
    // 区切り線
    if (col === 12 || col === 17 || col === 22) {
      e.cell.style.borderLeft = 'double #4c4c4c';
    }


    // 直近の点検の文字色を青色にする
    const highlightCurrentOrNextMonth = (value) => {
      if (!value) return false;
      const now = new Date();
      const current = `${String(now.getFullYear()).slice(2)}年${String(now.getMonth() + 1).padStart(2, '0')}月`;
      const next = `${String(now.getFullYear() + (now.getMonth() + 1 === 12 ? 1 : 0)).slice(2)}年${String((now.getMonth() + 2) % 13 || 1).padStart(2, '0')}月`;
      return value === current || value === next;
    };
    if (e.col === grid.columns.findIndex(col => col.binding === 'FULL_ADDRESS')) {
      if (item.FULL_ADDRESS?.includes('群馬県')) {
        e.cell.textContent = item.FULL_ADDRESS.replace('群馬県', '').trim();
      }
    }

    const planCols = {
      12: item.M6_PLAN_FORMAT,
      17: item.Y1_PLAN_FORMAT,
      22: item.Y3_PLAN_FORMAT,
    };

    if (Object.keys(planCols).includes(e.col.toString())) {
      const planValue = planCols[e.col];
      if (highlightCurrentOrNextMonth(planValue)) {
        e.cell.style.color = 'blue';
      }
    }
  }

  // 初期表示処理
  useEffect(() => {
    // 別画面から戻ってきた場合は検索条件を復活
    let condition = location?.state?.searchCondition ?? {
      PLAN_DATE_START: null,
      PLAN_DATE_END: null,
    };
    setSearchCondition(condition);
    // 検索
    SearchClients(condition, location?.state?.targetClientCd);
  }, []);

  // 他の画面から戻ってきた場合に顧客を選択
  useEffect(() => {
    selectTarget(targetKoujiNo);
  }, [targetKoujiNo]);

  const selectTarget = (targetKoujiNo) => {
    if (targetKoujiNo) {
      const grid = flexGrid.current.control;

      // 対象のSEIKYU_NOを持つ行のインデックスを探す
      const index = grid.rows.findIndex(
        (row) => row.dataItem?.AFTER_NO === targetKoujiNo
      );

      if (index >= 0) {
        setTimeout(() => {
          grid.select(new CellRange(index), true); // 対象行を選択状態にする
        }, 10);
      }
    }
  };

  // コード翻訳用のマッピング
  const planerWithEmpty = [...master.planer, { id: 0, name: '' }];
  const planerMap = new DataMap(planerWithEmpty, 'id', 'name');

  // 顧客情報ボタン
  const handleOnClickClientInfo = useCallback(() => {
    navigate('/admin/ClientInfo', {
      state: {
        clientId: flexGrid.current.control.selectedItems[0]['CLIENT_CD'],
        seikyuNo: flexGrid.current.control.selectedItems[0]['SEIKYU_NO'],
      },
    });
  }, [navigate]);

  // 検索ボタン
  const handleOnClickSearch = () => {
    SearchClients(searchCondition);
  };

  // 編集処理
  const handleChange = (prop) => (target) => {
    setSearchCondition({ ...searchCondition, [prop]: target?.value ?? target?.target.value });
  };

  // Excelダウンロード
  const handleExcelDownload = () => {
    const grid = flexGrid.current.control; // FlexGridコンポーネントへの参照を取得
    const fileName = 'アフター管理表.xlsx'; // ダウンロードするExcelファイルの名前

    // Excelファイルを生成し、ダウンロードする
    FlexGridXlsxConverter.save(
      grid,
      {
        includeColumns: function (column) {
          return column.header; // ヘッダー名称なしは含めない（ボタンの項目）
        },
      },
      fileName
    );
  };

  // アフター状況編集ボタン
  const handleOnClickStatusEdit = () => {
    const grid = flexGrid.current.control;
    const collectionView = grid.collectionView;

    // アフター状況編集モーダルの呼び出し処理
    setInspectionScheduleEditModalParam({
      call_list: true,
      inspectionData: grid.selectedItems[0], // 現在選択されている行のデータを渡す
      // アフター状況編集後のコールバック関数
      callback: (inspection) => {
        const [formattedInspection] = formatInspectionList([inspection]);

        const target = collectionView.items.find(
          (item) => item.SEIKYU_NO === inspection.SEIKYU_NO
        );

        if (target) {
          Object.assign(target, formattedInspection);
          collectionView.refresh();
          selectTarget(inspection.SEIKYU_NO);
        }
      },
    });
  };

  function changeDate(str) {
    if (!str) return null;

    const [y, m, d] = str.split('/').map(Number);
    if (!y || !m || !d) return null;

    return new Date(y, m - 1, d);
  }

  const getDateLimitFromPast = (targetDate) => {
    const year = targetDate.getFullYear() - 5;
    const month = targetDate.getMonth();
    const date = targetDate.getDate();

    return new Date(year, month, date);
  };

  const normalizeDateStr = (dateStr) => {
    // "-" または "/" で分割
    return dateStr.includes('-') ? dateStr.split('-') : dateStr.split('/');
  };

  const formatDateToYYM = (dateStr) => {
    if (!dateStr) return '';
    const [year, month] = normalizeDateStr(dateStr);
    const yy = year.slice(2);
    const m = month.padStart(2, '0');
    return `${yy}年${m}月`;
  };

  const formatDateToMMDD = (dateStr) => {
    if (!dateStr) return '';
    const [_, month, day] = normalizeDateStr(dateStr);
    const mm = month.padStart(2, '0');
    const dd = day.padStart(2, '0');
    return `${mm}/${dd}`;
  };

  const formatDateToYMD = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = normalizeDateStr(dateStr);
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}/${mm}/${dd}`;
  };

  const formatInspectionList = (data) => {
    const convertIfNotInspected = (item, flgKey, keys) => {
      if (item[flgKey] !== 1) {
        keys.forEach(key => {
          const val = item[key];
          if (val === '' || val == null) {
            item[key] = '-';
          }
        });
      }
    };

    return data.map((item) => {
      const formatted = {
        ...item,
        AFTER_NUMBER: Number(item.AFTER_NUMBER),
        HIKI_DATE_FORMAT: formatDateToYMD(item.HIKI_DATE),
        M6_PLAN_FORMAT: formatDateToYYM(item.M6_PLAN),
        Y1_PLAN_FORMAT: formatDateToYYM(item.Y1_PLAN),
        Y3_PLAN_FORMAT: formatDateToYYM(item.Y3_PLAN),
        M6_CARD_SEND_FORMAT: formatDateToYMD(item.M6_CARD_SEND),
        M6_CARD_RETURN_FORMAT: item.M6_CARD_RETURN_BIKO || formatDateToYMD(item.M6_CARD_RETURN),
        M6_INSPECT_DATE_FORMAT: formatDateToYMD(item.M6_INSPECT_DATE),
        Y1_CARD_SEND_FORMAT: formatDateToYMD(item.Y1_CARD_SEND),
        Y1_CARD_RETURN_FORMAT: item.Y1_CARD_RETURN_BIKO || formatDateToYMD(item.Y1_CARD_RETURN),
        Y1_INSPECT_DATE_FORMAT: formatDateToYMD(item.Y1_INSPECT_DATE),
        Y3_CARD_SEND_FORMAT: formatDateToYMD(item.Y3_CARD_SEND),
        Y3_CARD_RETURN_FORMAT: item.Y3_CARD_RETURN_BIKO || formatDateToYMD(item.Y3_CARD_RETURN),
        Y3_INSPECT_DATE_FORMAT: formatDateToYMD(item.Y3_INSPECT_DATE),
        M6_INSPECT_TAN_FORMAT: item.M6_INSPECT_TAN,
        Y1_INSPECT_TAN_FORMAT: item.Y1_INSPECT_TAN,
        Y3_INSPECT_TAN_FORMAT: item.Y3_INSPECT_TAN,
      };
      convertIfNotInspected(formatted, 'M6_INSPECTION_FLG', [
        'M6_PLAN_FORMAT', 'M6_CARD_SEND_FORMAT', 'M6_CARD_RETURN_FORMAT', 'M6_INSPECT_DATE_FORMAT', 'M6_INSPECT_TAN_FORMAT'
      ]);

      convertIfNotInspected(formatted, 'Y1_INSPECTION_FLG', [
        'Y1_PLAN_FORMAT', 'Y1_CARD_SEND_FORMAT', 'Y1_CARD_RETURN_FORMAT', 'Y1_INSPECT_DATE_FORMAT', 'Y1_INSPECT_TAN_FORMAT'
      ]);

      convertIfNotInspected(formatted, 'Y3_INSPECTION_FLG', [
        'Y3_PLAN_FORMAT', 'Y3_CARD_SEND_FORMAT', 'Y3_CARD_RETURN_FORMAT', 'Y3_INSPECT_DATE_FORMAT', 'Y3_INSPECT_TAN_FORMAT'
      ]);

      return formatted;
    });
  };

  return (
    <>
      <Row>
        <Col className="pl-3" md="2.5">
          <Form.Group>
            <label className="item-header-label">点検予定月</label>
            <div className="d-flex" style={{ width: '340px' }}>
              <TextComponent
                type="date"
                onChange={handleChange('PLAN_DATE_START')}
                selectionMode="Month"
              ></TextComponent>
              <span className="mt-2 ml-2 mr-2">～</span>
              <TextComponent
                type="date"
                onChange={handleChange('PLAN_DATE_END')}
                selectionMode="Month"
              ></TextComponent>
            </div>
          </Form.Group>
        </Col>
        <Col className="pr-1" xs="auto" style={{ width: '300px' }}>
          <Form.Group>
            <label className="item-header-label">絞り込み</label>
            <TextComponent
              placeholder="顧客名・住所・電話番号"
              type="text"
              value={searchCondition.KEYWORD}
              onChange={handleChange('KEYWORD')}
            ></TextComponent>
          </Form.Group>
        </Col>
        <Col className="pr-1" md="2">
          <Form.Group>
            <label className="item-header-label">アフターNO</label>
            <TextComponent
              type="numberString"
              maxLength={7}
              value={searchCondition.AFTER_NUMBER}
              onChange={handleChange('AFTER_NUMBER')}
            ></TextComponent>
          </Form.Group>
        </Col>
        <Col className="mt-2 pl-2 pr-2" md="1.5">
          <Form.Group>
            <label style={{ fontSize: '14pt', color: 'black' }}>
              <input
                className="mr-2"
                style={{ transform: 'scale(1.5)' }}
                type="checkbox"
                checked={isInspectionWithinOneMonth}
                onChange={() => setIsInspectionWithinOneMonth(!isInspectionWithinOneMonth)}
              ></input>
              今月から来月の点検予定を表示
            </label>
            <br />
            <label style={{ fontSize: '14pt', color: 'black' }}>
              <input
                className="mr-2"
                style={{ transform: 'scale(1.5)' }}
                type="checkbox"
                checked={includeCompletedInspections}
                onChange={() => setIncludeCompletedInspections(!includeCompletedInspections)}
              ></input>
              アフター完了分も含める
            </label>
          </Form.Group>
        </Col>
        <Col className="mt-1">
          <Form.Group>
            <br />
            <Button
              variant="success"
              className="btn-fill"
              size="sm"
              onClick={handleOnClickSearch}
            >
              検索
            </Button>

            {/* 入力中の検索条件を引き継いで各一覧へ遷移する */}
            <ScreenTransitionMenu
              current="InspectionSchedule"
              searchCondition={searchCondition}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            variant="success"
            className="btn-fill m-2"
            size="sm"
            onClick={handleExcelDownload}
          >
            Excelダウンロード
          </Button>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col>
          <div className="table-scroll">
            <FlexGrid
              ref={flexGrid}
              isReadOnly={true}
              itemsSource={inspectionRequests}
              selectionMode={'Row'}
              headersVisibility={'Column'}
              allowMerging="ColumnHeaders"
              initialized={onInitialized}
              formatItem={formatItem}
              style={{ height: '100%' }}
              loadedRows={(grid) => {
                grid.rows.forEach((ele) => {
                  ele.height = 38;
                });
              }}
            >
              <FlexGridFilter />
              <FlexGridColumn header="" binding="" isReadOnly={true} width={78}>
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(cell) => (
                    <span>
                      <Button
                        className="material-symbols-rounded cell-icon btn-fill"
                        variant="success"
                        title="顧客情報"
                        onClick={handleOnClickClientInfo}
                      >
                        person
                      </Button>
                      <Button
                        className="material-symbols-rounded cell-icon btn-fill"
                        variant="dark"
                        title="アフター状況編集"
                        onClick={handleOnClickStatusEdit}
                      >
                        more_horiz
                      </Button>
                    </span>
                  )}
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="AFTER_NUMBER"
                header="No"
                align="right"
                width={40}
                format="d"
              ></FlexGridColumn>
              <FlexGridColumn
                binding="CONTRACT_NUMBER"
                header="契約番号"
                align="right"
                width={65}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="HIKI_DATE_FORMAT"
                header="引渡日"
                width={75}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="SALES_TAN_CD"
                header="営業"
                width={48}
                align="center"
                allowResizing={false}
                dataMap={planerMap}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="GENBA_TAN_CD"
                header="現場"
                width={48}
                align="center"
                allowResizing={false}
                dataMap={planerMap}
              ></FlexGridColumn>
              <FlexGridColumn binding="NAME_1" header="御名前" width={88}></FlexGridColumn>
              <FlexGridColumn binding="POST_CODE" header="郵便番号" width={70}></FlexGridColumn>
              <FlexGridColumn binding="FULL_ADDRESS" header="ご住所・工事場所" width={152}></FlexGridColumn>
              <FlexGridColumn binding="TEL1" header="電話番号"></FlexGridColumn>
              <FlexGridColumn binding="IRAI_MEI" header="工事内容" width={84}></FlexGridColumn>
              <FlexGridColumn binding="ZEINUKI_KIN" header="金額(税抜)" width={80}></FlexGridColumn>
              <FlexGridColumn
                binding="M6_PLAN_FORMAT"
                header="予定月"
                allowResizing={false}
                align="center"
                width={60}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="M6_CARD_SEND_FORMAT"
                header="発送"
                allowResizing={false}
                align="center"
                width={50}
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) => ctx.item.M6_CARD_SEND_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.M6_CARD_SEND)}
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="M6_CARD_RETURN_FORMAT"
                header="返信"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) =>
                    ctx.item.M6_CARD_RETURN_BIKO
                      ? ctx.item.M6_CARD_RETURN_BIKO
                      : ctx.item.M6_CARD_RETURN_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.M6_CARD_RETURN)
                  }
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="M6_INSPECT_DATE_FORMAT"
                header="実施"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) => ctx.item.M6_INSPECT_DATE_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.M6_INSPECT_DATE)}
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="M6_INSPECT_TAN_FORMAT"
                header="担当"
                allowResizing={false}
                width={48}
                dataMap={planerMap}
                align="center"
              ></FlexGridColumn>
              <FlexGridColumn
                binding="Y1_PLAN_FORMAT"
                header="予定月"
                width={60}
                allowResizing={false}
                align="center"
              ></FlexGridColumn>
              <FlexGridColumn
                binding="Y1_CARD_SEND_FORMAT"
                header="発送"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) => ctx.item.Y1_CARD_SEND_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.Y1_CARD_SEND)}
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="Y1_CARD_RETURN_FORMAT"
                header="返信"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) =>
                    ctx.item.Y1_CARD_RETURN_BIKO
                      ? ctx.item.Y1_CARD_RETURN_BIKO
                      : ctx.item.Y1_CARD_RETURN_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.Y1_CARD_RETURN)
                  }
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="Y1_INSPECT_DATE_FORMAT"
                header="実施"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) => ctx.item.Y1_INSPECT_DATE_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.Y1_INSPECT_DATE)}
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="Y1_INSPECT_TAN_FORMAT"
                header="担当"
                allowResizing={false}
                align="center"
                width={48}
                dataMap={planerMap}
              ></FlexGridColumn>
              <FlexGridColumn
                binding="Y3_PLAN_FORMAT"
                header="予定月"
                width={60}
                allowResizing={false}
                align="center"
              ></FlexGridColumn>
              <FlexGridColumn
                binding="Y3_CARD_SEND_FORMAT"
                header="発送"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) => ctx.item.Y3_CARD_SEND_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.Y3_CARD_SEND)}
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="Y3_CARD_RETURN_FORMAT"
                header="返信"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) =>
                    ctx.item.Y3_CARD_RETURN_BIKO
                      ? ctx.item.Y3_CARD_RETURN_BIKO
                      : ctx.item.Y3_CARD_RETURN_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.Y3_CARD_RETURN)
                  }
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="Y3_INSPECT_DATE_FORMAT"
                header="実施"
                width={50}
                allowResizing={false}
                align="center"
              >
                <FlexGridCellTemplate
                  cellType="Cell"
                  template={(ctx) => ctx.item.Y3_INSPECT_DATE_FORMAT === '-' ? '-' : formatDateToMMDD(ctx.item.Y3_INSPECT_DATE)}
                />
              </FlexGridColumn>
              <FlexGridColumn
                binding="Y3_INSPECT_TAN_FORMAT"
                header="担当"
                allowResizing={false}
                width={48}
                dataMap={planerMap}
                align="center"
              ></FlexGridColumn>
            </FlexGrid>
          </div>
        </Col>
      </Row>
      {/** 工事状況モーダル */}
      <InspectionScheduleEditModal params={inspectionScheduleEditModalParam} />
    </>
  );
};

export default InspectionScheduleList;
